import { Header } from "@general-backoffice/core/index";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";

// Billing data components (for prefilling wizard and payment method creation check)
import useMyBillingData from "../../../../components/billingData/hooks/useMyBillingData";
import ChargesTableCard from "../../../../components/charges/ChargesTableCard";
// Payment history components
import useMyCharges from "../../../../components/charges/hooks/useMyCharges";
import CreateUserPaymentMethodModal from "../../../../components/paymentMethods/CreateUserPaymentMethodModal";
import usePaymentMethod from "../../../../components/paymentMethods/hooks/usePaymentMethod";
// Payment methods components
import usePaymentMethods from "../../../../components/paymentMethods/hooks/usePaymentMethods";
import PaymentMethodsCard from "../../../../components/paymentMethods/PaymentMethodsCard";
import RemovePaymentMethodModal from "../../../../components/paymentMethods/RemovePaymentMethodModal";
import { TaxProfileCard } from "../../../../components/referrer/taxProfile";
// Refund history components
import useRefunds from "../../../../components/refunds/hooks/useRefunds";
import RefundsTableCard from "../../../../components/refunds/RefundsTableCard";
import useUser from "../../../../components/users/hooks/useUser";
import type { RootState } from "../../../../redux/reducers/session/state";

/**
 * BillingNew - New billing page with tax compliance features.
 * Will eventually replace the old ManagePayments page.
 */
const BillingNew: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "billingNew" });
  const { t: tManagePayments } = useTranslation("dashboard", { keyPrefix: "managePayments" });
  const { user: sessionUser } = useSelector((state: RootState) => state.sessionState);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    load: refreshUser,
    isLoading: isLoadingUser,
  } = useUser("me", {
    autoload: true,
    loadParams: {
      populate: ["taxProfile"],
    },
  });

  // Billing data hooks (for prefilling wizard)
  const { billingData, isLoading: isLoadingBillingData } = useMyBillingData("me");

  // Check for openWizard query param to auto-open wizard in TaxProfileCard
  const [autoOpenWizard, setAutoOpenWizard] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openWizard") === "true" && !isLoadingBillingData) {
      setAutoOpenWizard(true);
    }
  }, [location.search, isLoadingBillingData]);

  // Callback when wizard auto-opens - clear URL param
  const handleWizardAutoOpened = useCallback(() => {
    setAutoOpenWizard(false);
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname]);

  // Payment methods hooks
  const {
    paymentMethods,
    refresh: refreshPaymentMethods,
    isLoading: isLoadingPaymentMethods,
  } = usePaymentMethods({
    filters: sessionUser?.id
      ? {
          pm_owner: { id: { $eq: sessionUser.id } },
        }
      : undefined,
  });
  const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false);
  const {
    paymentMethod: paymentMethodToRemove,
    setPaymentMethod: setPaymentMethodToRemove,
    remove: removePaymentMethod,
    isLoading: isDeletingPaymentMethod,
  } = usePaymentMethod(undefined);

  // Payment history (charges) hooks
  const { charges, ...chargesProps } = useMyCharges({
    pagination: true,
    sort: { createdAt: "down" },
  });

  // Refund history hooks
  const { refunds, ...refundsProps } = useRefunds({
    pagination: { page: 1, pageSize: 10 },
    mine: true,
  });

  return (
    <React.Fragment>
      {/* Payment Method Modals */}
      <CreateUserPaymentMethodModal
        isOpen={isCreatingPaymentMethod}
        close={() => setIsCreatingPaymentMethod(false)}
        onCreated={async () => {
          setIsCreatingPaymentMethod(false);
          refreshPaymentMethods().catch(() => null);
        }}
      />
      <RemovePaymentMethodModal
        paymentMethod={paymentMethodToRemove}
        close={() => setPaymentMethodToRemove(null)}
        remove={async () => {
          await removePaymentMethod();
          await refreshPaymentMethods();
          setPaymentMethodToRemove(null);
        }}
        isLoading={isDeletingPaymentMethod}
      />

      <Header.BreadCrumb color="secondary" isDark={false} />
      <Container className="mt--6" fluid>
        {isLoadingUser ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Row>
              {/* Tax Profile Card */}
              <Col xs={12} lg={6}>
                <TaxProfileCard
                  user={user}
                  onUpdate={() => {
                    refreshUser();
                  }}
                  isLoading={isLoadingUser}
                  billingData={billingData}
                  autoOpenWizard={autoOpenWizard}
                  onWizardAutoOpened={handleWizardAutoOpened}
                />
              </Col>

              {/* Payment Methods Card */}
              <Col xs={12} lg={6}>
                <PaymentMethodsCard
                  paymentMethods={paymentMethods}
                  isLoading={isLoadingPaymentMethods}
                  setPaymentMethodToCreate={billingData?.name || user?.taxProfile?.taxProfileComplete ? () => setIsCreatingPaymentMethod(true) : undefined}
                  setPaymentMethodToRemove={setPaymentMethodToRemove}
                />
              </Col>

              {/* Payment History Table */}
              <Col xs={12}>
                <ChargesTableCard {...chargesProps} title={tManagePayments("paymentsTableTitle")} charges={charges} />
              </Col>

              {/* Refund History Table. Rendered unconditionally so the card
                  owns its own loading and empty states; gating on truthiness
                  made its empty message unreachable and shifted the layout as
                  the section appeared after the charges table. */}
              <Col xs={12}>
                <RefundsTableCard
                  {...refundsProps}
                  title={tManagePayments("refundsTableTitle")}
                  refunds={refunds ?? []}
                />
              </Col>

              {/* Info Card */}
              <Col xs={12}>
                <Card className="shadow">
                  <CardHeader className="bg-transparent border-0">
                    <h3 className="mb-0">{t("infoSection.title", "About the New Billing System")}</h3>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <h5>
                            <i className="fas fa-file-invoice text-primary mr-2" />
                            {t("infoSection.taxCompliance.title", "Tax Compliance")}
                          </h5>
                          <p className="text-muted text-sm">
                            {t(
                              "infoSection.taxCompliance.description",
                              "Complete your tax profile to receive properly formatted invoices compliant with Spanish tax regulations."
                            )}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <h5>
                            <i className="fas fa-euro-sign text-success mr-2" />
                            {t("infoSection.autofactura.title", "Autofactura")}
                          </h5>
                          <p className="text-muted text-sm">
                            {t(
                              "infoSection.autofactura.description",
                              "For referrers: claim your commissions and receive automatically generated invoices with proper VAT and IRPF calculations."
                            )}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <h5>
                            <i className="fas fa-check-circle text-info mr-2" />
                            {t("infoSection.vies.title", "EU VAT Verification")}
                          </h5>
                          <p className="text-muted text-sm">
                            {t(
                              "infoSection.vies.description",
                              "EU businesses can verify their VAT number through VIES for proper intra-community invoicing."
                            )}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default BillingNew;
